import { useEffect, useState, useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { Navbar } from "@/components/layout/Navbar"
import { Footer } from "@/components/layout/Footer"
import { PageLayout } from "@/components/layout/PageLayout"
import { MainContent } from "@/components/layout/MainContent"
import { GameCard } from "@/components/layout/GameCard"
import { GameCardSkeleton } from "@/components/layout/GameCardSkeleton"
import { useNavigate } from "react-router-dom"
import { useAuthStore } from "@/stores/auth.store"
import { useTeamsStore } from "@/stores/teams.store"
import { gameToCardProps, getGamePath, getSportTheme, PENDING_PAYMENT_STATUSES } from "@/utils/game.utils"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { gameService } from "@/services/game.service"
import { tournamentService, type TournamentListItem } from "@/services/tournament.service"
import {
  tournamentRegistrationService,
  type TournamentRegistration,
} from "@/services/tournament-registration.service"
import { tournamentToCardProps, getTournamentExplorePath } from "@/utils/tournament-card.utils"
import { isConfirmedTournamentRegistration } from "@/utils/tournament.utils"
import {
  trainingClassToCardProps,
  trainingSessionToCardProps,
} from "@/utils/training-card.utils"
import { getTrainingClassPath, getTrainingSessionPath } from "@/utils/training.utils"
import { trainingClassService, trainingEnrollmentService, trainingSessionService } from "@/services/training.service"
import type { TrainingClass, TrainingEnrollment, TrainingSession } from "@/types/training"
import { Game, GetGamesFilter } from "@/types/game"
import { PageHeading } from "@/components/layout/PageHeading"
import { DashboardFilters, type HomeContentFilter } from "@/components/dashboard/DashboardFilters"
import { AmbientBackground } from "@/components/ui/ambient-background"
import { HomeNewClientCta } from "@/components/home/HomeNewClientCta"

type GameWithParticipants = Game & {
  currentPlayers?: number
  isRegistered?: boolean
  userParticipantStatus?: string | null
  paymentExpiresAt?: string | null
}

type HomeFeedItem =
  | { kind: "game"; sortAt: number; game: GameWithParticipants }
  | { kind: "tournament"; sortAt: number; tournament: TournamentListItem }
  | { kind: "event"; sortAt: number; session: TrainingSession }
  | { kind: "training"; sortAt: number; trainingClass: TrainingClass }

function pickTrainingEnrollmentMap(
  enrollments: TrainingEnrollment[] | undefined,
  key: "sessionId" | "classId",
): Map<string, TrainingEnrollment> {
  const map = new Map<string, TrainingEnrollment>()
  for (const enrollment of enrollments ?? []) {
    const id = key === "sessionId" ? enrollment.sessionId : enrollment.classId
    if (!id || enrollment.status === "CANCELLED") continue
    const existing = map.get(id)
    if (!existing) {
      map.set(id, enrollment)
      continue
    }
    if (enrollment.status === "PENDING_PAYMENT") {
      map.set(id, enrollment)
    } else if (existing.status !== "PENDING_PAYMENT" && enrollment.status === "CONFIRMED") {
      map.set(id, enrollment)
    }
  }
  return map
}

function toTrainingCardRegistration(enrollment?: TrainingEnrollment) {
  if (!enrollment) return undefined
  return {
    isRegistered: true,
    userEnrollmentStatus: enrollment.status,
    paymentExpiresAt: enrollment.paymentExpiresAt ?? null,
  }
}

export default function HomePage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { teams, fetchTeams } = useTeamsStore()

  const [filters, setFilters] = useState<GetGamesFilter>({})
  const [contentFilter, setContentFilter] = useState<HomeContentFilter>("all")
  const [pendingPaymentOnly, setPendingPaymentOnly] = useState(false)

  const sportTheme = getSportTheme(filters.sport, "Home")

  const {
    data: games = [],
    isLoading,
    isError,
    error,
    refetch,
    isFetched,
  } = useQuery({
    queryKey: ["publicGames", filters],
    queryFn: () => gameService.findPublic(filters),
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 60,
    placeholderData: (previousData) => previousData,
  })

  const tournamentFilterParams = useMemo(
    () => ({
      sport: filters.sport,
      startDate: filters.startDate,
      endDate: filters.endDate,
    }),
    [filters.sport, filters.startDate, filters.endDate],
  )

  const {
    data: tournaments = [],
    isLoading: tournamentsLoading,
    isError: tournamentsIsError,
    error: tournamentsError,
    refetch: refetchTournaments,
    isFetched: tournamentsFetched,
  } = useQuery({
    queryKey: ["publicTournaments", tournamentFilterParams],
    queryFn: () => tournamentService.findPublic(tournamentFilterParams),
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 60,
    placeholderData: (previousData) => previousData,
  })

  const trainingFilterParams = useMemo(
    () => ({ sport: filters.sport }),
    [filters.sport],
  )

  const eventFilterParams = useMemo(
    () => ({
      sport: filters.sport,
      from: filters.startDate,
      to: filters.endDate,
    }),
    [filters.sport, filters.startDate, filters.endDate],
  )

  const {
    data: trainingClasses = [],
    isLoading: trainingLoading,
    isError: trainingIsError,
    error: trainingError,
    refetch: refetchTraining,
    isFetched: trainingFetched,
  } = useQuery({
    queryKey: ["publicTrainingClasses", trainingFilterParams],
    queryFn: () => trainingClassService.listPublicAll(trainingFilterParams),
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 60,
    placeholderData: (previousData) => previousData,
  })

  const {
    data: events = [],
    isLoading: eventsLoading,
    isError: eventsIsError,
    error: eventsError,
    refetch: refetchEvents,
    isFetched: eventsFetched,
  } = useQuery({
    queryKey: ["publicTrainingEvents", eventFilterParams],
    queryFn: () => trainingSessionService.listPublic(eventFilterParams),
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 60,
    placeholderData: (previousData) => previousData,
  })

  useEffect(() => {
    if (user) fetchTeams()
  }, [user, fetchTeams])

  const handleFilterChange = (newFilters: GetGamesFilter) => {
    setFilters(newFilters)
  }

  // Jogos ativos (antes de bater no backend p/ buscar participantes)
  const activeGames = useMemo(() => {
    if (!games?.length) return []

    const now = new Date()

    return games.filter((game) => {
      const gameDate = new Date(game.gameDate)
      const gameEndDate = game.gameEndDate ? new Date(game.gameEndDate) : null

      // se tiver fim do jogo, valida por ele; se não tiver, usa gameDate
      const ended = gameEndDate ? gameEndDate < now : gameDate < now
      return !ended
    })
  }, [games])

  // Status de participante com cache (evita flicker "Inscrições Abertas" → "Inscrito")
  const gameIds = useMemo(
    () => activeGames.map((g) => g.id).sort(),
    [activeGames]
  )
  const participantStatusQuery = useQuery({
    queryKey: ["participantStatus", user?.id, gameIds],
    queryFn: () => gameService.getParticipantStatusBatch(activeGames.map((g) => g.id)),
    enabled: !!user && activeGames.length > 0,
    staleTime: 1000 * 60, // 1 min: status não muda tão rápido
    gcTime: 1000 * 60 * 10,
  })

  const myTournamentRegistrationsQuery = useQuery({
    queryKey: ["myTournamentRegistrations", user?.id],
    queryFn: () => tournamentRegistrationService.myRegistrations(),
    enabled: !!user,
    staleTime: 1000 * 60,
    gcTime: 1000 * 60 * 10,
  })

  const myEventEnrollmentsQuery = useQuery({
    queryKey: ["myEventEnrollments", user?.id],
    queryFn: () => trainingEnrollmentService.myEventEnrollments(),
    enabled: !!user,
    staleTime: 1000 * 60,
    gcTime: 1000 * 60 * 10,
  })

  const myClassEnrollmentsQuery = useQuery({
    queryKey: ["myClassEnrollments", user?.id],
    queryFn: () => trainingEnrollmentService.myClassEnrollments(),
    enabled: !!user,
    staleTime: 1000 * 60,
    gcTime: 1000 * 60 * 10,
  })

  const tournamentRegistrationById = useMemo(() => {
    const map = new Map<string, TournamentRegistration>()
    for (const reg of myTournamentRegistrationsQuery.data ?? []) {
      if (reg.status === "CANCELLED" || reg.status === "REFUNDED") continue
      const existing = map.get(reg.tournamentId)
      if (!existing) {
        map.set(reg.tournamentId, reg)
        continue
      }
      if (reg.status === "PENDING_PAYMENT") {
        map.set(reg.tournamentId, reg)
        continue
      }
      if (isConfirmedTournamentRegistration(reg.status) && !isConfirmedTournamentRegistration(existing.status)) {
        map.set(reg.tournamentId, reg)
      }
    }
    return map
  }, [myTournamentRegistrationsQuery.data])

  const eventEnrollmentBySessionId = useMemo(
    () => pickTrainingEnrollmentMap(myEventEnrollmentsQuery.data, "sessionId"),
    [myEventEnrollmentsQuery.data],
  )

  const classEnrollmentByClassId = useMemo(
    () => pickTrainingEnrollmentMap(myClassEnrollmentsQuery.data, "classId"),
    [myClassEnrollmentsQuery.data],
  )

  const teamRoleByTeamId = useMemo(() => {
    const map = new Map<string, string>()
    for (const team of teams) {
      if (team.userRole) map.set(team.id, team.userRole)
    }
    return map
  }, [teams])

  const gamesWithParticipants = useMemo(() => {
    const statusMap = participantStatusQuery.data ?? {}
    return activeGames.map((game) => {
      const participant = statusMap[game.id]
      return {
        ...game,
        currentPlayers: game.confirmedCount ?? 0,
        isRegistered: user ? participant != null : false,
        userParticipantStatus: participant?.status ?? null,
        paymentExpiresAt: participant?.paymentExpiresAt ?? null,
      } as GameWithParticipants
    })
  }, [activeGames, participantStatusQuery.data, user])

  const loadingParticipants =
    !!user && activeGames.length > 0 && participantStatusQuery.isLoading && !participantStatusQuery.data

  const loadingTournamentRegistrations =
    !!user &&
    tournaments.length > 0 &&
    myTournamentRegistrationsQuery.isLoading &&
    !myTournamentRegistrationsQuery.data

  const loadingTrainingEnrollments =
    !!user &&
    (myEventEnrollmentsQuery.isLoading || myClassEnrollmentsQuery.isLoading) &&
    !myEventEnrollmentsQuery.data &&
    !myClassEnrollmentsQuery.data

  const activeEvents = useMemo(() => {
    const now = Date.now()
    return events.filter((session) => {
      const startsAt = new Date(session.startsAt).getTime()
      return !Number.isNaN(startsAt) && startsAt >= now
    })
  }, [events])

  const mergedFeed = useMemo((): HomeFeedItem[] => {
    const items: HomeFeedItem[] = []
    const gameIds = new Set<string>()
    const tournamentIds = new Set<string>()
    const eventIds = new Set<string>()
    const classIds = new Set<string>()

    for (const game of gamesWithParticipants) {
      gameIds.add(game.id)
      items.push({
        kind: "game",
        sortAt: new Date(game.gameDate).getTime(),
        game,
      })
    }
    for (const tournament of tournaments) {
      tournamentIds.add(tournament.id)
      const raw = tournament.startsAt?.trim()
      let sortAt = Number.MAX_SAFE_INTEGER
      if (raw) {
        const t = new Date(raw).getTime()
        if (!Number.isNaN(t)) sortAt = t
      }
      items.push({ kind: "tournament", sortAt, tournament })
    }
    for (const session of activeEvents) {
      eventIds.add(session.id)
      items.push({
        kind: "event",
        sortAt: new Date(session.startsAt).getTime(),
        session,
      })
    }
    for (const trainingClass of trainingClasses) {
      classIds.add(trainingClass.id)
      const raw = trainingClass.startsOn?.trim()
      let sortAt = Number.MAX_SAFE_INTEGER
      if (raw) {
        const t = new Date(raw).getTime()
        if (!Number.isNaN(t)) sortAt = t
      }
      items.push({ kind: "training", sortAt, trainingClass })
    }

    if (user) {
      for (const enrollment of myEventEnrollmentsQuery.data ?? []) {
        if (enrollment.status !== "PENDING_PAYMENT" || !enrollment.sessionId || !enrollment.session) {
          continue
        }
        if (eventIds.has(enrollment.sessionId)) continue
        const startsAt = new Date(enrollment.session.startsAt).getTime()
        items.push({
          kind: "event",
          sortAt: Number.isNaN(startsAt) ? Number.MAX_SAFE_INTEGER : startsAt,
          session: enrollment.session,
        })
        eventIds.add(enrollment.sessionId)
      }

      for (const enrollment of myClassEnrollmentsQuery.data ?? []) {
        if (enrollment.status !== "PENDING_PAYMENT" || !enrollment.classId || !enrollment.trainingClass) {
          continue
        }
        if (classIds.has(enrollment.classId)) continue
        const raw = enrollment.trainingClass.startsOn?.trim()
        let sortAt = Number.MAX_SAFE_INTEGER
        if (raw) {
          const t = new Date(raw).getTime()
          if (!Number.isNaN(t)) sortAt = t
        }
        items.push({
          kind: "training",
          sortAt,
          trainingClass: enrollment.trainingClass,
        })
        classIds.add(enrollment.classId)
      }
    }

    items.sort((a, b) => a.sortAt - b.sortAt)
    return items
  }, [
    gamesWithParticipants,
    tournaments,
    activeEvents,
    trainingClasses,
    user,
    myEventEnrollmentsQuery.data,
    myClassEnrollmentsQuery.data,
  ])

  const isFeedItemPendingPayment = useMemo(() => {
    const participantStatus = participantStatusQuery.data ?? {}
    return (item: HomeFeedItem): boolean => {
      if (item.kind === "game") {
        const status = participantStatus[item.game.id]?.status ?? item.game.userParticipantStatus
        return PENDING_PAYMENT_STATUSES.includes((status ?? "") as never)
      }
      if (item.kind === "tournament") {
        return tournamentRegistrationById.get(item.tournament.id)?.status === "PENDING_PAYMENT"
      }
      if (item.kind === "event") {
        return eventEnrollmentBySessionId.get(item.session.id)?.status === "PENDING_PAYMENT"
      }
      return classEnrollmentByClassId.get(item.trainingClass.id)?.status === "PENDING_PAYMENT"
    }
  }, [
    participantStatusQuery.data,
    tournamentRegistrationById,
    eventEnrollmentBySessionId,
    classEnrollmentByClassId,
  ])

  const pendingPaymentCount = useMemo(
    () => mergedFeed.filter((item) => isFeedItemPendingPayment(item)).length,
    [mergedFeed, isFeedItemPendingPayment],
  )

  useEffect(() => {
    if (pendingPaymentCount === 0 && pendingPaymentOnly) {
      setPendingPaymentOnly(false)
    }
  }, [pendingPaymentCount, pendingPaymentOnly])

  const filteredFeed = useMemo(() => {
    let items = mergedFeed

    if (contentFilter === "games") items = items.filter((item) => item.kind === "game")
    else if (contentFilter === "tournaments") items = items.filter((item) => item.kind === "tournament")
    else if (contentFilter === "events") items = items.filter((item) => item.kind === "event")
    else if (contentFilter === "training") items = items.filter((item) => item.kind === "training")

    if (pendingPaymentOnly) {
      items = items.filter((item) => isFeedItemPendingPayment(item))
    } else if (user && pendingPaymentCount > 0) {
      items = [...items].sort((a, b) => {
        const aPending = isFeedItemPendingPayment(a)
        const bPending = isFeedItemPendingPayment(b)
        if (aPending === bPending) return a.sortAt - b.sortAt
        return aPending ? -1 : 1
      })
    }

    return items
  }, [
    mergedFeed,
    contentFilter,
    pendingPaymentOnly,
    isFeedItemPendingPayment,
    user,
    pendingPaymentCount,
  ])

  const contentCounts = useMemo(
    () => ({
      games: gamesWithParticipants.length,
      tournaments: tournaments.length,
      events: activeEvents.length,
      training: trainingClasses.length,
    }),
    [gamesWithParticipants.length, tournaments.length, activeEvents.length, trainingClasses.length],
  )

  const showEmpty =
    isFetched &&
    tournamentsFetched &&
    trainingFetched &&
    eventsFetched &&
    !isLoading &&
    !tournamentsLoading &&
    !trainingLoading &&
    !eventsLoading &&
    !isError &&
    !tournamentsIsError &&
    !trainingIsError &&
    !eventsIsError &&
    !loadingParticipants &&
    !loadingTournamentRegistrations &&
    !loadingTrainingEnrollments &&
    filteredFeed.length === 0

  const listLoading =
    filteredFeed.length === 0 &&
    !loadingParticipants &&
    !loadingTournamentRegistrations &&
    !loadingTrainingEnrollments &&
    (isLoading || tournamentsLoading || trainingLoading || eventsLoading)

  const errorMessage =
    isError &&
    ((error as any)?.response?.data?.message ||
      (error instanceof Error ? error.message : "Erro ao carregar jogos públicos"))

  const tournamentsErrorMessage =
    tournamentsIsError &&
    ((tournamentsError as any)?.response?.data?.message ||
      (tournamentsError instanceof Error ? tournamentsError.message : "Erro ao carregar torneios"))

  const trainingErrorMessage =
    trainingIsError &&
    ((trainingError as any)?.response?.data?.message ||
      (trainingError instanceof Error ? trainingError.message : "Erro ao carregar treinamentos"))

  const eventsErrorMessage =
    eventsIsError &&
    ((eventsError as any)?.response?.data?.message ||
      (eventsError instanceof Error ? eventsError.message : "Erro ao carregar eventos"))

  return (

    <PageLayout 
      className="bg-background relative overflow-hidden min-h-screen"
      backgroundBlobs={
        <AmbientBackground 
          blob1Color={filters.sport ? sportTheme.blob1 : undefined} 
          blob2Color={filters.sport ? sportTheme.blob2 : undefined} 
        />
      }
    >
      {/* Ambient Background Blobs */}
      <Navbar showAvatar />

      {/* Main Content */}
      <MainContent>
        {/* Page Heading */}
        <div className="flex flex-col gap-3 sm:gap-4">
          <PageHeading
            title="Explorar"
            description="Jogos, torneios, eventos e treinamentos perto de você."
            className="mb-0"
          >
            <DashboardFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              contentFilter={contentFilter}
              onContentFilterChange={setContentFilter}
              contentCounts={contentCounts}
              pendingPaymentOnly={pendingPaymentOnly}
              onPendingPaymentOnlyChange={user ? setPendingPaymentOnly : undefined}
              pendingPaymentCount={pendingPaymentCount}
            />
          </PageHeading>

          <HomeNewClientCta />
        </div>

        {/* Error Message */}
        {isError && errorMessage && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-4 rounded-xl font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm border border-red-100 dark:border-red-900/30">
            <p className="flex-1">{errorMessage}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                refetch()
              }}
              className="shrink-0 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30"
            >
              Tentar novamente
            </Button>
          </div>
        )}

        {tournamentsIsError && tournamentsErrorMessage && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-4 rounded-xl font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm border border-red-100 dark:border-red-900/30">
            <p className="flex-1">{tournamentsErrorMessage}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchTournaments()}
              className="shrink-0 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30"
            >
              Tentar novamente
            </Button>
          </div>
        )}

        {trainingIsError && trainingErrorMessage && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-4 rounded-xl font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm border border-red-100 dark:border-red-900/30">
            <p className="flex-1">{trainingErrorMessage}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchTraining()}
              className="shrink-0 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30"
            >
              Tentar novamente
            </Button>
          </div>
        )}

        {eventsIsError && eventsErrorMessage && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-4 rounded-xl font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm border border-red-100 dark:border-red-900/30">
            <p className="flex-1">{eventsErrorMessage}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchEvents()}
              className="shrink-0 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30"
            >
              Tentar novamente
            </Button>
          </div>
        )}

        {/* Empty State */}
        {showEmpty && (
          <EmptyState
            icon={pendingPaymentOnly ? "payments" : "explore"}
            title={pendingPaymentOnly ? "Nenhum pagamento pendente" : "Nada por aqui"}
            description={
              pendingPaymentOnly
                ? "Você não tem inscrições aguardando pagamento no momento."
                : "Não há atividades com estes filtros no momento. Tente outro tipo ou ajuste esporte e data."
            }
          />
        )}

        {/* Skeletons quando a lista mista ainda não tem itens */}
        {listLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <GameCardSkeleton key={index} />
            ))}
          </div>
        )}

        {filteredFeed.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-4">
            {filteredFeed.map((item, index) => {
              if (item.kind === "game") {
                const game = item.game
                const handleButtonClick = () => {
                  if (!user) {
                    navigate("/login")
                    return
                  }
                  navigate(getGamePath(game))
                }
                return (
                  <GameCard
                    key={`game-${game.id}`}
                    {...gameToCardProps(
                      game,
                      handleButtonClick,
                      game.currentPlayers,
                      game.isRegistered,
                      game.userParticipantStatus,
                      game.paymentExpiresAt,
                      "Continuar",
                      {
                        userId: user?.id,
                        userTeamRole: teamRoleByTeamId.get(game.teamId),
                      },
                    )}
                    imagePriority={index < 4}
                    onCardClick={() => navigate(getGamePath(game))}
                  />
                )
              }

              if (item.kind === "tournament") {
                const tournament = item.tournament
                const myReg = tournamentRegistrationById.get(tournament.id)
                const goToTournament = () => {
                  const registration = tournamentRegistrationById.get(tournament.id)
                  navigate(
                    getTournamentExplorePath(tournament, {
                      userRegistrationStatus: registration?.status,
                    }),
                  )
                }
                return (
                  <GameCard
                    key={`tournament-${tournament.id}`}
                    {...tournamentToCardProps(
                      tournament,
                      goToTournament,
                      myReg
                        ? {
                            isRegistered: true,
                            userRegistrationStatus: myReg.status,
                            paymentExpiresAt: myReg.paymentExpiresAt,
                          }
                        : undefined,
                    )}
                    imagePriority={index < 4}
                    onCardClick={goToTournament}
                  />
                )
              }

              if (item.kind === "event") {
                const session = item.session
                const myEnrollment = eventEnrollmentBySessionId.get(session.id)
                const goToEvent = () => navigate(getTrainingSessionPath(session))
                return (
                  <GameCard
                    key={`event-${session.id}`}
                    {...trainingSessionToCardProps(
                      session,
                      goToEvent,
                      toTrainingCardRegistration(myEnrollment),
                    )}
                    imagePriority={index < 4}
                    onCardClick={goToEvent}
                  />
                )
              }

              const trainingClass = item.trainingClass
              const teamId = trainingClass.teamId ?? trainingClass.team?.id
              const myClassEnrollment = classEnrollmentByClassId.get(trainingClass.id)
              const goToTraining = () => {
                if (!teamId) return
                navigate(getTrainingClassPath(teamId, trainingClass.id))
              }
              return (
                <GameCard
                  key={`training-${trainingClass.id}`}
                  {...trainingClassToCardProps(
                    trainingClass,
                    goToTraining,
                    toTrainingCardRegistration(myClassEnrollment),
                  )}
                  imagePriority={index < 4}
                  onCardClick={teamId ? goToTraining : undefined}
                />
              )
            })}
          </div>
        )}
      </MainContent>
      <Footer />
    </PageLayout>
  )
}
